import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardMedia,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  ButtonGroup,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  FolderOpen as BrowseIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { S3ImageBrowser } from './S3ImageBrowser';

// =============================================================================
// INTERFACES
// =============================================================================
interface ImageFieldProps {
  value?: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================
export const ImageField: React.FC<ImageFieldProps> = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset error state
    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setUploading(true);

    try {
      // Step 1: Get presigned URL from server (bypasses CloudFront for the actual upload)
      const presignedResponse = await api.post('/upload/presigned-url', {
        fileName: file.name,
        contentType: file.type,
        folder: 'images' // Specific folder for images
      });

      const { uploadUrl, finalUrl } = presignedResponse.data;

      // Step 2: Upload directly to S3 using presigned URL (bypasses CloudFront)
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      // Step 3: Use the final URL
      onChange(finalUrl);
    } catch (error: any) {
      console.error('Upload failed:', error);
      setError(error.response?.data?.error || error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    if (!value) return;

    try {
      await api.delete('/upload/delete', {
        data: { url: value },
      });
      onChange('');
      setError(null);
    } catch (error: any) {
      console.error('Delete failed:', error);
      setError(error.response?.data?.error || 'Failed to delete image');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleBrowseClick = () => {
    setBrowserOpen(true);
  };

  const handleBrowserClose = () => {
    setBrowserOpen(false);
  };

  const handleImageSelect = (imageUrl: string) => {
    onChange(imageUrl);
    setError(null);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label} {required && '*'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {value ? (
        <Card sx={{ maxWidth: 300, mb: 2 }}>
          <CardMedia
            component="img"
            height="200"
            image={value}
            alt={label}
            sx={{ objectFit: 'contain' }}
          />
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Image selected
            </Typography>
            <Tooltip title="Remove image">
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                disabled={disabled}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Card>
      ) : (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            bgcolor: 'background.paper',
            minHeight: 150,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            '&:hover': disabled ? {} : {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
          onClick={disabled ? undefined : handleUploadClick}
        >
          {uploading ? (
            <>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography color="text.secondary">Uploading...</Typography>
            </>
          ) : (
            <>
              <ImageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Click to upload an image or browse existing images
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supports: JPG, PNG, GIF (max 10MB)
              </Typography>
            </>
          )}
        </Box>
      )}

      <ButtonGroup variant="outlined" sx={{ mt: 1 }}>
        <Button
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          onClick={handleUploadClick}
          disabled={disabled || uploading}
        >
          {uploading ? 'Uploading...' : value ? 'Change Image' : 'Upload Image'}
        </Button>
        <Button
          startIcon={<BrowseIcon />}
          onClick={handleBrowseClick}
          disabled={disabled}
        >
          Browse
        </Button>
      </ButtonGroup>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      <S3ImageBrowser
        open={browserOpen}
        onClose={handleBrowserClose}
        onSelectImage={handleImageSelect}
        title={`Browse Images for ${label}`}
      />
    </Box>
  );
}; 